import type { AppRole } from "@/lib/roles";
import {
  Home, Package, PlusCircle, Truck, Radar, Route as RouteIcon,
  Sparkles, Users, Settings, MapPin, Shield, FileCheck,
  AlertTriangle, MessageSquare, BarChart3, ClipboardCheck, ScrollText, Crown,
  BadgeCheck, Brain, Zap, LifeBuoy, Wallet, Bell, QrCode, Camera,
  ShieldCheck, Banknote, Gauge, TrendingUp, Sliders, DollarSign, Briefcase,
  LayoutDashboard, CircleUser,
} from "lucide-react";

export type NavIcon = React.ComponentType<{ className?: string }>;

export interface NavEntry {
  to: string;
  label: string;
  icon: NavIcon;
  roles: AppRole[] | "all";
  end?: boolean;
  /** primary = a role destination in the rail; secondary = contextual child. */
  tier: "primary" | "secondary";
  /** id of the owning role destination (domain) for contextual sub-navigation. */
  domain?: string;
}

/**
 * VX-2 role destination model (Moveload One UI).
 *
 * Each role gets a short set of task-oriented destinations; everything else
 * lives *inside* a destination as a contextual child, so no existing URL
 * becomes unreachable.
 *
 *   Shipper      Home | Shipments | Book | Track | Account
 *   Transporter  Home | Work | Fleet | Money | Insights
 *   Driver       Today | Scan | Map | POD | Account
 *   Founder      Now | Network | Trust | Money | Growth | System
 */
export interface NavDomain {
  id: string;
  label: string;
  icon: NavIcon;
  to: string;
  end?: boolean;
  roles: AppRole[] | "all";
  children: Omit<NavEntry, "roles" | "tier" | "domain">[];
}

const SHARED_ACCOUNT_CHILDREN = [
  { to: "/app/settings", label: "Account & settings", icon: Settings },
  { to: "/app/security", label: "Security centre", icon: Shield },
  { to: "/app/notifications", label: "Notifications", icon: Bell },
  { to: "/app/support", label: "Messages & support", icon: LifeBuoy },
  { to: "/app/documents", label: "Documents", icon: FileCheck },
  { to: "/policies", label: "Policies", icon: ScrollText },
];

export const NAV_DOMAINS: NavDomain[] = [
  // ============================ Shipper ============================
  { id: "shipper-home", label: "Home", icon: Home, to: "/app/shipper", end: true, roles: ["shipper"], children: [
    { to: "/app/intelligence", label: "Intelligence", icon: Sparkles },
  ]},
  { id: "shipper-shipments", label: "Shipments", icon: Package, to: "/app/shipper/shipments", roles: ["shipper"], children: [] },
  { id: "shipper-book", label: "Book", icon: PlusCircle, to: "/app/shipper/create", roles: ["shipper"], children: [] },
  { id: "shipper-track", label: "Track", icon: RouteIcon, to: "/app/shipper/shipments?view=live", roles: ["shipper"], children: [] },
  { id: "shipper-account", label: "Account", icon: CircleUser, to: "/app/settings", roles: ["shipper"], children: SHARED_ACCOUNT_CHILDREN.slice(1) },

  // ========================== Transporter ==========================
  { id: "transporter-home", label: "Home", icon: Home, to: "/app/transporter", end: true, roles: ["transporter"], children: [
    { to: "/app/transporter/preview", label: "Preview workspace", icon: Truck },
  ]},
  { id: "transporter-work", label: "Work", icon: Briefcase, to: "/app/transporter/opportunities", roles: ["transporter"], children: [
    { to: "/app/transporter/tms", label: "Trips", icon: RouteIcon, end: true },
    { to: "/app/transporter/tms/create", label: "New own-fleet load", icon: PlusCircle },
    { to: "/app/transporter/availability", label: "Availability", icon: MapPin },
    { to: "/app/transporter/return-journey", label: "Return journey search", icon: Radar },
    { to: "/app/driver/scan", label: "Scan pickup", icon: QrCode },
  ]},
  { id: "transporter-fleet", label: "Fleet", icon: Truck, to: "/app/transporter/fleet", roles: ["transporter"], children: [
    { to: "/app/transporter/preferences", label: "Match preferences", icon: Sliders },
  ]},
  { id: "transporter-money", label: "Money", icon: Wallet, to: "/app/transporter/earnings", roles: ["transporter"], children: [
    { to: "/app/transporter/trust", label: "Verification", icon: Wallet },
  ]},
  { id: "transporter-insights", label: "Insights", icon: Brain, to: "/app/intelligence", roles: ["transporter"], children: [
    { to: "/app/transporter/return-loads", label: "Return-load intelligence", icon: Radar },
    ...SHARED_ACCOUNT_CHILDREN,
  ]},

  // ============================= Driver =============================
  { id: "driver-today", label: "Today", icon: Home, to: "/app/driver", end: true, roles: ["driver"], children: [
    { to: "/app/driver/mobile", label: "Mobile workspace", icon: QrCode, end: true },
    { to: "/app/driver/mobile/queue", label: "Offline queue", icon: Zap },
  ]},
  { id: "driver-scan", label: "Scan", icon: QrCode, to: "/app/driver/scan", roles: ["driver"], children: [
    { to: "/app/driver/mobile/scan", label: "Mobile scan", icon: QrCode },
  ]},
  { id: "driver-map", label: "Map", icon: MapPin, to: "/app/driver/mobile/tracking", roles: ["driver"], children: [] },
  { id: "driver-pod", label: "POD", icon: Camera, to: "/app/driver/mobile/pod", roles: ["driver"], children: [] },
  { id: "driver-account", label: "Account", icon: CircleUser, to: "/app/settings", roles: ["driver"], children: SHARED_ACCOUNT_CHILDREN.slice(1) },

  // ============================= Founder ============================
  { id: "founder-now", label: "Now", icon: Crown, to: "/app/founder", end: true, roles: ["founder_owner"], children: [] },
  { id: "founder-network", label: "Network", icon: Gauge, to: "/app/founder/delivery-confidence", roles: ["founder_owner"], children: [] },
  { id: "founder-trust", label: "Trust", icon: ShieldCheck, to: "/app/founder/security", roles: ["founder_owner"], children: [] },
  { id: "founder-money", label: "Money", icon: Banknote, to: "/app/founder/pricing-centre", roles: ["founder_owner"], children: [] },
  { id: "founder-growth", label: "Growth", icon: TrendingUp, to: "/app/founder/growth", roles: ["founder_owner"], children: [] },
  { id: "founder-system", label: "System", icon: Sliders, to: "/app/founder/switches", roles: ["founder_owner"], children: SHARED_ACCOUNT_CHILDREN },

  // ============================== Admin =============================
  { id: "admin-home", label: "Command centre", icon: LayoutDashboard, to: "/app/admin", end: true,
    roles: ["ops_admin", "finance_admin", "compliance_admin", "super_admin"], children: [
      { to: "/app/admin/kpis", label: "KPIs", icon: BarChart3 },
      { to: "/app/admin/readiness", label: "Readiness", icon: ClipboardCheck },
    ]},
  { id: "admin-freight", label: "Freight", icon: Package, to: "/app/admin/shipments", roles: ["ops_admin", "super_admin"], children: [
    { to: "/app/admin/orchestration", label: "Orchestration", icon: Brain },
    { to: "/app/admin/availability", label: "Availability", icon: MapPin },
    { to: "/app/admin/carriers", label: "Carriers", icon: Truck },
    { to: "/app/admin/incidents", label: "Incidents", icon: AlertTriangle },
    { to: "/app/admin/automation", label: "Automation", icon: Zap },
  ]},
  { id: "admin-money", label: "Money", icon: DollarSign, to: "/app/admin/finance",
    roles: ["finance_admin", "ops_admin", "super_admin"], children: [] },
  { id: "admin-trust", label: "Compliance", icon: Shield, to: "/app/admin/compliance",
    roles: ["compliance_admin", "super_admin"], children: [
      { to: "/app/join-requests", label: "Join requests", icon: BadgeCheck },
    ]},
  { id: "admin-people", label: "People", icon: Users, to: "/app/admin/crm", roles: ["ops_admin", "super_admin"], children: [
    { to: "/app/admin/templates", label: "Templates", icon: MessageSquare },
    { to: "/app/intelligence", label: "Intelligence", icon: Sparkles },
    ...SHARED_ACCOUNT_CHILDREN,
  ]},
];

const visibleTo = (roles: AppRole[] | "all", role: AppRole | null) =>
  roles === "all" || (!!role && roles.includes(role));

/** Role destinations (the adaptive rail). */
export const navDomainsFor = (role: AppRole | null): NavDomain[] =>
  NAV_DOMAINS.filter((d) => visibleTo(d.roles, role));

/** Flat entry list, derived from the domain model. */
export const NAV_ENTRIES: NavEntry[] = NAV_DOMAINS.flatMap((domain) => [
  { to: domain.to, label: domain.label, icon: domain.icon, end: domain.end, roles: domain.roles, tier: "primary" as const, domain: domain.id },
  ...domain.children.map((c) => ({ ...c, roles: domain.roles, tier: "secondary" as const, domain: domain.id })),
]);

const dedupe = (entries: NavEntry[]) => {
  const seen = new Set<string>();
  return entries.filter((e) => (seen.has(e.to) ? false : (seen.add(e.to), true)));
};

export const primaryNavFor = (role: AppRole | null) =>
  dedupe(NAV_ENTRIES.filter((e) => e.tier === "primary" && visibleTo(e.roles, role)));

export const secondaryNavFor = (role: AppRole | null) => {
  const primary = new Set(primaryNavFor(role).map((e) => e.to));
  return dedupe(NAV_ENTRIES.filter((e) => e.tier === "secondary" && visibleTo(e.roles, role))).filter(
    (e) => !primary.has(e.to),
  );
};

/** Contextual children of the destination that currently owns the pathname. */
export const contextualNavFor = (role: AppRole | null, pathname: string): NavEntry[] => {
  const domains = navDomainsFor(role);
  const owner = domains
    .filter((d) => {
      const base = d.to.split("?")[0];
      return d.end ? pathname === base : pathname === base || pathname.startsWith(`${base}/`);
    })
    .sort((a, b) => b.to.length - a.to.length)[0];
  if (!owner) return [];
  return owner.children.map((c) => ({ ...c, roles: owner.roles, tier: "secondary" as const, domain: owner.id }));
};

// ===================== Breadcrumbs =====================

/** Static labels for known path segments; unknown segments are humanised. */
export const SEGMENT_LABELS: Record<string, string> = {
  app: "Moveload",
  shipper: "Shipper",
  transporter: "Transporter",
  driver: "Driver",
  admin: "Administration",
  founder: "Founder command",
  create: "Book a load",
  shipments: "Shipments",
  fleet: "Fleet & drivers",
  availability: "Availability",
  trust: "Verification",
  preview: "Preview workspace",
  opportunities: "Work",
  preferences: "Match preferences",
  "return-journey": "Return journey",
  "return-loads": "Return loads",
  tms: "Trips",
  scan: "Scan pickup",
  pod: "Proof of delivery",
  mobile: "Mobile",
  queue: "Offline queue",
  tracking: "Map",
  orchestration: "Orchestration",
  carriers: "Carriers",
  finance: "Payments & payouts",
  compliance: "Compliance",
  crm: "CRM",
  incidents: "Incidents",
  templates: "Templates",
  kpis: "KPIs",
  readiness: "Readiness",
  automation: "Automation",
  intelligence: "Insights",
  support: "Messages & support",
  notifications: "Notifications",
  security: "Security",
  "join-requests": "Join requests",
  documents: "Documents",
  settings: "Account & settings",
  launch: "Launch readiness",
  "audit-intelligence": "Audit & risk",
  "autonomy-safety": "Autonomy safety",
  switches: "Kill switches",
  rules: "Protected rules",
  pricing: "MIPE pricing",
  "pricing-centre": "Pricing centre",
  "pricing-automation": "Pricing automation",
  payouts: "Payouts",
  log: "Action log",
  verification: "Verification",
  flags: "Feature flags",
  approvals: "Approvals",
  versions: "Versions",
  reliability: "Reliability",
  "scale-sre": "Scale & SRE",
  adversarial: "Adversarial",
  "delivery-confidence": "Delivery confidence",
  "customer-success": "Customer success",
  growth: "Growth",
  proofs: "Proofs",
  simulator: "Simulator",
  runbook: "Runbook",
  impersonate: "Impersonation",
  reveal: "Reveal & export",
};

const humanise = (segment: string) =>
  segment.replace(/-/g, " ").replace(/^\w/, (c) => c.toUpperCase());

const looksLikeId = (segment: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}/i.test(segment) || /^\d+$/.test(segment);

export interface Crumb { label: string; to?: string }

/** Builds a breadcrumb trail from a pathname. The last crumb has no link. */
export function buildBreadcrumbs(pathname: string): Crumb[] {
  const segments = pathname.split("/").filter(Boolean);
  if (!segments.length) return [];
  const crumbs: Crumb[] = [];
  let acc = "";
  segments.forEach((segment, i) => {
    acc += `/${segment}`;
    const label = looksLikeId(segment)
      ? `#${segment.slice(0, 8)}`
      : SEGMENT_LABELS[segment] ?? humanise(segment);
    crumbs.push({ label, to: i === segments.length - 1 ? undefined : acc });
  });
  return crumbs;
}
